import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Filter, RefreshCw, Search, ShieldCheck, X, MessageSquareQuote } from "lucide-react";
import { PageHeader, KpiCard } from "../../components/common";
import { PaginationBar, TableRowsSkeleton, usePagedList } from "../../components/PaginationBar";
import { FetchButton, FetchDirtyHint, FetchForm, FetchPrompt, useFetchGate } from "../../components/FetchBar";
import { ActorLine, categoryOf } from "../../components/AuditTimeline";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, downloadBlob, errMsg, fmtDateTime } from "../../lib/api";
import { staffLabelText } from "../../components/StaffAvatar";

/**
 * Phase 90 — Admin Audit Trail: permanent, filterable record of who did what, when and why.
 * Backed by GET /t/{code}/audit-trail (paginated) + /export (Excel).
 */
const CATEGORY_TONE = {
  payment: "bg-emerald-100 text-emerald-800 border-emerald-200",
  reassessment: "bg-sky-100 text-sky-800 border-sky-200",
  survey: "bg-indigo-100 text-indigo-800 border-indigo-200",
  transfer: "bg-violet-100 text-violet-800 border-violet-200",
  field: "bg-amber-100 text-amber-800 border-amber-200",
  property: "bg-teal-100 text-teal-800 border-teal-200",
  cheque: "bg-lime-100 text-lime-800 border-lime-200",
  user: "bg-slate-100 text-slate-700 border-slate-200",
  settings: "bg-slate-100 text-slate-700 border-slate-200",
  default: "bg-muted text-muted-foreground border-border",
};

const EMPTY = { actor_id: "all", category: "all", pin: "", q: "", date_from: "", date_to: "", with_rationale: false };

export default function AuditTrail() {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => ({
    ...EMPTY,
    actor_id: searchParams.get("actor_id") || "all",
    category: searchParams.get("category") || "all",
    pin: searchParams.get("pin") || "",
  }));
  const [actors, setActors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get(`/t/${code}/audit-trail/actors`).then((r) => setActors(r.data)).catch(() => {});
    api.get(`/t/${code}/audit-trail/categories`).then((r) => setCategories(r.data)).catch(() => {});
  }, [code]);

  // Phase 133: the trail loads only on Fetch (arriving with ?actor_id / ?category / ?pin counts as that click)
  const gate = useFetchGate(filters, { autoFetch: !!(searchParams.get("actor_id") || searchParams.get("category") || searchParams.get("pin")) });
  const params = useMemo(() => {
    const f = gate.applied;
    const p = {};
    if (!f) return p;
    if (f.actor_id !== "all") p.actor_id = f.actor_id;
    if (f.category !== "all") p.category = f.category;
    if (f.pin.trim()) p.pin = f.pin.trim();
    if (f.q.trim()) p.q = f.q.trim();
    if (f.date_from) p.date_from = f.date_from;
    if (f.date_to) p.date_to = f.date_to;
    if (f.with_rationale) p.with_rationale = true;
    return p;
  }, [gate.appliedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = usePagedList(`/t/${code}/audit-trail`, { params, initialLimit: 50, enabled: gate.fetched, reloadKey: gate.version });
  const active = Object.keys(params).length;
  const liveActive = Object.entries(filters).filter(([k, v]) => v && v !== "all" && v !== false).length;
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const clearAll = () => { setFilters(EMPTY); gate.reset(); };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(Object.entries(params).map(([k, v]) => [k, String(v)])).toString();
      await downloadBlob(`/t/${code}/audit-trail/export${qs ? `?${qs}` : ""}`, `audit-trail-${code}.xlsx`);
      toast.success("Audit trail exported");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  const staffCount = actors.filter((a) => !a.platform).length;
  const inactiveCount = actors.filter((a) => !a.platform && !a.is_active).length;

  return (
    <div data-testid="audit-trail-page">
      <PageHeader title="Audit Trail" subtitle="Permanent, tamper-proof record of who did what, when and why — collections, assessments, approvals and configuration changes.">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={list.reload} disabled={list.loading || !gate.fetched} data-testid="audit-refresh-button">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${list.loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button size="sm" onClick={exportXlsx} disabled={exporting || !gate.fetched || list.total === 0} data-testid="audit-export-button">
            <Download className="h-3.5 w-3.5 mr-1.5" /> {exporting ? "Preparing…" : "Export Excel"}
          </Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiCard label="Matching entries" value={gate.fetched ? list.total.toLocaleString("en-IN") : "—"} sub={gate.fetched ? undefined : "click Fetch to load"} icon={ShieldCheck} testId="audit-kpi-total" />
        <KpiCard label="Staff on record" value={staffCount} sub={inactiveCount ? `${inactiveCount} deactivated / former` : "all active"} testId="audit-kpi-staff" />
        <KpiCard label="Retention" value="Permanent" sub="Cannot be edited or wiped" testId="audit-kpi-retention" />
        <KpiCard label="Decision rationale" value="Required" sub="Approve & reject, min 10 chars" testId="audit-kpi-rationale" />
      </div>

      <Card className="shadow-sm mb-4">
        <FetchForm onFetch={gate.fetch} testId="audit-filter-form">
        <CardContent className="p-4 space-y-3" data-testid="audit-filter-bar">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Performed by</Label>
              <Select value={filters.actor_id} onValueChange={(v) => set({ actor_id: v })}>
                <SelectTrigger data-testid="audit-actor-filter"><SelectValue placeholder="All staff" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All staff</SelectItem>
                  {actors.map((a) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`audit-actor-option-${a.id}`}>
                      {staffLabelText(a.name, a.employee_id)}{a.role_label ? ` — ${a.role_label}` : ""}{a.deleted ? " (former)" : a.is_active === false ? " (deactivated)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Category</Label>
              <Select value={filters.category} onValueChange={(v) => set({ category: v })}>
                <SelectTrigger data-testid="audit-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((c) => <SelectItem key={c.key} value={c.key} data-testid={`audit-category-option-${c.key}`}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Property PIN</Label>
              <Input value={filters.pin} onChange={(e) => set({ pin: e.target.value })} placeholder="e.g. BMC100014" className="font-mono" data-testid="audit-pin-filter" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={filters.q} onChange={(e) => set({ q: e.target.value })} placeholder="name, employee ID, receipt, rationale…" className="pl-8" data-testid="audit-search-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => set({ date_from: e.target.value })} data-testid="audit-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => set({ date_to: e.target.value })} data-testid="audit-date-to" />
            </div>
            <div className="space-y-1 flex flex-col justify-end">
              <label className="flex items-center gap-2 text-sm h-10 cursor-pointer select-none">
                <Switch checked={filters.with_rationale} onCheckedChange={(v) => set({ with_rationale: v })} data-testid="audit-rationale-only-switch" />
                <span className="flex items-center gap-1"><MessageSquareQuote className="h-3.5 w-3.5 text-muted-foreground" /> Decisions with rationale only</span>
              </label>
            </div>
            <div className="flex items-end justify-end gap-2 flex-wrap">
              <FetchDirtyHint dirty={gate.dirty} testId="audit-fetch-dirty" />
              {liveActive > 0 && (
                <Button type="button" variant="ghost" size="sm" className="h-10" onClick={clearAll} data-testid="audit-clear-filters">
                  <X className="h-3.5 w-3.5 mr-1" /> Clear {liveActive} filter{liveActive === 1 ? "" : "s"}
                </Button>
              )}
              <FetchButton type="submit" loading={list.loading && gate.fetched} dirty={gate.dirty} fetched={gate.fetched} testId="audit-fetch-button" />
            </div>
          </div>
        </CardContent>
        </FetchForm>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          {!gate.fetched ? (
            <FetchPrompt onFetch={gate.fetch} testId="audit-fetch-prompt"
              subtitle="Pick who, what, which property and when above, then click Fetch to load the audit entries." />
          ) : list.loading && !list.data ? (
            <TableRowsSkeleton rows={8} cols={5} testId="audit-table-skeleton" />
          ) : list.error ? (
            <div className="p-8 text-center space-y-2" data-testid="audit-error">
              <p className="text-sm text-destructive">{errMsg(list.error)}</p>
              <Button size="sm" variant="outline" onClick={list.reload}>Retry</Button>
            </div>
          ) : list.items.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground" data-testid="audit-empty">
              <Filter className="h-6 w-6 mx-auto mb-2 opacity-50" />
              No audit entries match these filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">When (IST)</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Performed by</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead className="min-w-[280px]">What / Why</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.items.map((r) => {
                    const cat = categoryOf(r.action);
                    return (
                      <TableRow key={r.id} data-testid={`audit-row-${r.id}`} data-action={r.action}>
                        <TableCell className="text-xs tabular-nums whitespace-nowrap align-top">{fmtDateTime(r.at)}</TableCell>
                        <TableCell className="align-top">
                          <Badge variant="outline" className={`text-[11px] font-medium ${CATEGORY_TONE[cat] || CATEGORY_TONE.default}`} data-testid="audit-row-event">{r.label}</Badge>
                          <p className="text-[10px] text-muted-foreground font-mono mt-1">{r.action}</p>
                        </TableCell>
                        <TableCell className="align-top text-xs"><ActorLine actor={r.actor} code={code} /></TableCell>
                        <TableCell className="align-top text-xs font-mono">{r.pin || <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="align-top text-xs">
                          {r.summary && <p className="break-words" data-testid="audit-row-summary">{r.summary}</p>}
                          {r.rationale && (
                            <p className="mt-1 rounded-md border bg-muted/40 px-2 py-1" data-testid="audit-row-rationale">
                              <span className="text-muted-foreground">Rationale:</span> {r.rationale}
                            </p>
                          )}
                          {!r.summary && !r.rationale && <span className="text-muted-foreground">—</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          {gate.fetched && (
            <div className="px-4 pb-3">
              <PaginationBar page={list.page} limit={list.limit} total={list.total} onPageChange={list.setPage}
                onLimitChange={list.setLimit} loading={list.loading} testId="audit-pagination" sizes={[25, 50, 100, 200]} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
